/**
 * FACTURA DETAIL SHEET - Panel lateral con detalle completo de una factura
 * Sigue el mismo patrón UX que orden-detail-sheet
 */

import { useTranslation } from "react-i18next";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Building2,
  FileText,
  CreditCard,
  Receipt,
  TestTubes,
  CheckCircle2,
  Clock,
} from "lucide-react";
import { useFactura } from "@/hooks/use-facturacion";
import type { EstadoFactura, TipoPago } from "@/hooks/use-facturacion";

// ---------------------------------------------------------------------------
// Helpers de variantes
// ---------------------------------------------------------------------------

const ESTADO_VARIANT: Record<EstadoFactura, "default" | "secondary" | "destructive" | "outline"> = {
  BORRADOR: "outline",
  EMITIDA: "secondary",
  PAGADA: "default",
  ANULADA: "destructive",
};

const TIPO_PAGO_LABEL: Record<TipoPago, string> = {
  ANTICIPO: "Anticipo",
  ABONO: "Abono",
  CANCELACION: "Cancelación",
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface FacturaDetailSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  facturaId: number | null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function FacturaDetailSheet({ open, onOpenChange, facturaId }: FacturaDetailSheetProps) {
  const { t } = useTranslation();
  const { data: factura, isLoading } = useFactura(facturaId || 0, {
    enabled: !!facturaId && open,
  });

  const subtotal = parseFloat(factura?.subtotal ?? "0");
  const descuento = parseFloat(factura?.descuento ?? "0");
  const descuentoPagosDirectos = parseFloat(factura?.descuento_pagos_directos ?? "0");
  const impuesto = parseFloat(factura?.impuesto ?? "0");
  const exento = parseFloat(factura?.exento ?? "0");
  const total = parseFloat(factura?.total ?? "0");
  const totalPagado = parseFloat(factura?.total_pagado ?? "0");
  const saldoPendiente = parseFloat(factura?.saldo_pendiente ?? "0");

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-[560px] overflow-y-auto p-0">
        <SheetHeader className="px-4 sm:px-6 pt-6 pb-0">
          <SheetTitle>{t("facturacion.detail.title")}</SheetTitle>
          <SheetDescription>
            {isLoading ? <Skeleton className="h-4 w-48" /> : t("facturacion.detail.description")}
          </SheetDescription>
        </SheetHeader>

        {/* Loading skeleton */}
        {isLoading && (
          <div className="px-4 sm:px-6 py-4 space-y-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </div>
        )}

        {factura && (
          <div className="px-4 sm:px-6 pb-6 space-y-5 mt-4">
            {/* ── Número + estado + PDF ─────────────────────────────── */}
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="font-mono text-xl font-bold tracking-tight">{factura.numero}</span>
                <Badge variant={ESTADO_VARIANT[factura.estado]}>
                  {t(`facturacion.estados.${factura.estado}`)}
                </Badge>
              </div>
            </div>

            {/* ── Datos generales ───────────────────────────────────── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <div>
                <p className="text-muted-foreground text-xs">{t("facturacion.detail.fecha")}</p>
                <p className="font-medium mt-0.5">{factura.fecha}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">
                  {t("facturacion.detail.tipoDocumento")}
                </p>
                <p className="font-medium mt-0.5">{factura.tipo_documento}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">{t("facturacion.detail.paciente")}</p>
                <p className="font-medium mt-0.5">{factura.paciente_nombre}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">{t("facturacion.detail.sucursal")}</p>
                <p className="font-medium mt-0.5">{factura.sucursal_nombre}</p>
              </div>
              <div className="sm:col-span-2">
                <p className="text-muted-foreground text-xs">{t("facturacion.detail.cai")}</p>
                <p className="font-mono text-xs mt-0.5 break-all">{factura.cai_codigo}</p>
              </div>
            </div>

            {/* ── Datos fiscales ────────────────────────────────────── */}
            <div className="rounded-lg border bg-muted/30 p-3 space-y-2 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <Building2 className="h-4 w-4" />
                <span className="font-medium text-foreground">
                  {t("facturacion.detail.datosFiscales")}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                <div>
                  <p className="text-muted-foreground text-xs">{t("facturacion.detail.cliente")}</p>
                  <p className="font-medium mt-0.5">{factura.nombre_cliente || "-"}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">{t("facturacion.detail.rtn")}</p>
                  <p className="font-mono mt-0.5">{factura.rtn_cliente || "-"}</p>
                </div>
                {factura.cliente && (
                  <div className="col-span-2">
                    <p className="text-muted-foreground text-xs">
                      {t("facturacion.detail.clienteEmpresa")}
                    </p>
                    <p className="font-medium mt-0.5">{factura.cliente.nombre}</p>
                  </div>
                )}
              </div>
            </div>

            <Separator />

            {/* ── Órdenes asociadas ─────────────────────────────────── */}
            {factura.ordenes.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <h4 className="text-sm font-semibold">
                    {t("facturacion.detail.ordenes")} ({factura.ordenes.length})
                  </h4>
                </div>
                <div className="space-y-1.5">
                  {factura.ordenes.map((orden) => (
                    <div
                      key={orden.id}
                      className="flex items-center justify-between rounded-lg bg-muted/50 px-3 py-2 text-sm"
                    >
                      <span className="font-mono font-medium">{orden.numero}</span>
                      <div className="flex items-center gap-3 text-muted-foreground text-xs">
                        <span>{orden.fecha}</span>
                        <span className="font-mono font-medium text-foreground">
                          L {parseFloat(orden.total).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Separator />

            {/* ── Detalles (exámenes) ───────────────────────────────── */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <TestTubes className="h-4 w-4 text-muted-foreground" />
                <h4 className="text-sm font-semibold">
                  {t("facturacion.detail.detalles")} ({factura.detalles.length})
                </h4>
              </div>

              {/* Agrupar por orden */}
              {(() => {
                const grupos: Record<string, typeof factura.detalles> = {};
                for (const d of factura.detalles) {
                  const key = d.orden_numero ?? "";
                  if (!grupos[key]) grupos[key] = [];
                  grupos[key].push(d);
                }
                return Object.entries(grupos).map(([ordenNumero, detalles]) => (
                  <div key={ordenNumero} className="mb-4">
                    {ordenNumero && (
                      <p className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wide">
                        {t("facturacion.detail.ordenNumero", { numero: ordenNumero })}
                      </p>
                    )}
                    <div className="space-y-1.5">
                      {detalles.map((detalle) => (
                        <div
                          key={detalle.id}
                          className="flex items-center justify-between rounded-lg border p-2.5 text-sm bg-muted/20"
                        >
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{detalle.descripcion}</p>
                            <p className="text-muted-foreground text-xs mt-0.5">
                              {detalle.cantidad} × L{" "}
                              {parseFloat(detalle.precio_unitario).toFixed(2)}
                              {parseFloat(detalle.descuento) > 0 && (
                                <span className="text-green-600 ml-1">
                                  − L {parseFloat(detalle.descuento).toFixed(2)}
                                </span>
                              )}
                            </p>
                          </div>
                          <span className="font-mono font-medium ml-3 shrink-0">
                            L {parseFloat(detalle.subtotal).toFixed(2)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ));
              })()}

              {/* Resumen de totales */}
              <div className="mt-3 pt-3 border-t space-y-1.5 text-sm">
                <div className="flex justify-between text-muted-foreground">
                  <span>{t("facturacion.detail.subtotal")}</span>
                  <span className="tabular-nums">L {subtotal.toFixed(2)}</span>
                </div>
                {descuento > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>{t("facturacion.detail.descuento")}</span>
                    <span className="tabular-nums">− L {descuento.toFixed(2)}</span>
                  </div>
                )}
                {descuentoPagosDirectos > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>{t("facturacion.detail.descuentoPagosDirectos")}</span>
                    <span className="tabular-nums">− L {descuentoPagosDirectos.toFixed(2)}</span>
                  </div>
                )}
                {impuesto > 0 && (
                  <div className="flex justify-between text-muted-foreground">
                    <span>{t("facturacion.detail.impuesto")}</span>
                    <span className="tabular-nums">L {impuesto.toFixed(2)}</span>
                  </div>
                )}
                {exento > 0 && (
                  <div className="flex justify-between text-muted-foreground">
                    <span>{t("facturacion.detail.exento")}</span>
                    <span className="tabular-nums">L {exento.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between pt-1 border-t font-semibold text-base">
                  <span>{t("facturacion.detail.total")}</span>
                  <span className="tabular-nums">L {total.toFixed(2)}</span>
                </div>
              </div>
            </div>

            <Separator />

            {/* ── Pagos ─────────────────────────────────────────────── */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <CreditCard className="h-4 w-4 text-muted-foreground" />
                <h4 className="text-sm font-semibold">{t("facturacion.detail.pagos")}</h4>
              </div>

              {factura.pagos.length === 0 ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                  <Clock className="h-4 w-4" />
                  <span>{t("facturacion.detail.noPagos")}</span>
                </div>
              ) : (
                <div className="space-y-1.5">
                  {factura.pagos.map((pago) => {
                    // Parse fecha de forma segura
                    const fechaPago = pago.fecha ? new Date(pago.fecha) : null;
                    const fechaValida = fechaPago && !isNaN(fechaPago.getTime());
                    const fechaFormateada = fechaValida
                      ? fechaPago.toLocaleString("es-HN", {
                          year: "numeric",
                          month: "2-digit",
                          day: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : pago.fecha || "-";

                    // Parse monto de forma segura
                    const montoPago = parseFloat(pago.monto);
                    const montoValido = !isNaN(montoPago);

                    return (
                      <div
                        key={pago.id}
                        className="flex items-center justify-between rounded-lg border p-3 text-sm"
                      >
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{pago.metodo_nombre}</p>
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                              {TIPO_PAGO_LABEL[pago.tipo_pago]}
                            </Badge>
                          </div>
                          <p className="text-muted-foreground text-xs mt-0.5">
                            {fechaFormateada}
                            {pago.referencia && ` · ${pago.referencia}`}
                          </p>
                        </div>
                        <span className="font-mono font-medium ml-3 shrink-0">
                          L {montoValido ? montoPago.toFixed(2) : "0.00"}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Resumen de saldo */}
              <div className="mt-3 pt-3 border-t space-y-1.5 text-sm">
                <div className="flex justify-between text-muted-foreground">
                  <span>{t("facturacion.detail.totalPagado")}</span>
                  <span className="tabular-nums text-foreground">L {totalPagado.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-semibold">
                  <span>{t("facturacion.detail.saldoPendiente")}</span>
                  <span
                    className={`tabular-nums flex items-center gap-1 ${
                      saldoPendiente > 0 ? "text-orange-600" : "text-green-600"
                    }`}
                  >
                    {saldoPendiente <= 0 && <CheckCircle2 className="h-3.5 w-3.5" />}L{" "}
                    {saldoPendiente.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            {/* ── Observaciones ─────────────────────────────────────── */}
            {factura.observaciones && (
              <>
                <Separator />
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Receipt className="h-4 w-4 text-muted-foreground" />
                    <h4 className="text-sm font-semibold">
                      {t("facturacion.detail.observaciones")}
                    </h4>
                  </div>
                  <p className="text-sm text-muted-foreground whitespace-pre-line leading-relaxed">
                    {factura.observaciones}
                  </p>
                </div>
              </>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
